import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface ImageModel {
    fields: {
        image: string;
        caption: string;
    };
}

const enum fields {
    imageCard = "imageCard",
    image = "image",
    caption = "caption",
    numberOfImages = "numberOfImages",
    generalLandingPage = "generalLandingPage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfImages]: number;
        [fields.imageCard]: ImageModel[];
        [fields.generalLandingPage]: boolean;
    };
}

type Props = OwnProps;

export interface ArticlesImageGridStyles {
    imagesContainer?: GridContainerProps;
    imageListContainerWith3Images?: GridItemProps;
    image?: LazyImageProps;
    captionCss?: InjectableCss;
    imageListContainerWith2Images?: GridItemProps;
    generalLandingPageImage?: LazyImageProps;
}

export const articlesImageGridStyles: ArticlesImageGridStyles = {
    imagesContainer: {
        gap: 30,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 40px;
                    `,
                    css`
                        margin-top: 40px;
                    `,
                    css`
                        margin-top: 40px;
                    `,
                    css`
                        margin-top: 50px;
                    `,
                    css`
                        margin-top: 50px;
                    `,
                ])}
        `,
    },

    imageListContainerWith3Images: {
        width: [12, 12, 4, 4, 4],
    },
    imageListContainerWith2Images: {
        width: [12, 12, 6, 6, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding-right: 0;
                    `,
                    css`
                        padding-right: 0;
                    `,
                ])}
        `,
    },
    image: {
        css: css`
            border: 1px solid #e5e5e5;
            > img {
                object-fit: cover;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            min-height: 477px;
                        `,
                        css`
                            min-height: 477px;
                        `,
                        css`
                            min-height: 477px;
                        `,
                        css`
                            min-height: 407px;
                            max-height: 407px;
                        `,
                        css`
                            min-height: 407px;
                            max-height: 407px;
                        `,
                    ])}
            }
        `,
    },

    generalLandingPageImage: {
        css: css`
            border: 1px solid #e5e5e5;
            > img {
                object-fit: cover;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            min-height: 304px;
                        `,
                        css`
                            min-height: 304px;
                        `,
                        css`
                            min-height: 304px;
                        `,
                        css`
                            min-height: 259px;
                        `,
                        css`
                            min-height: 259px;
                        `,
                    ])}
            }
        `,
    },

    captionCss: {
        css: css`
            color: #999999;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 24px;
            padding: 0 14px;
            font-weight: 100;
        `,
    },
};

const styles = articlesImageGridStyles;

const INTArticlesImageGrid: React.FC<Props> = ({ fields }) => {
    if (fields.imageCard.length === 0) {
        return null;
    }
    return (
        <GridContainer {...styles.imagesContainer}>
            {fields.imageCard.map((image, index) => (
                <GridItem
                    {...(fields?.numberOfImages === 3
                        ? { ...styles.imageListContainerWith3Images }
                        : { ...styles.imageListContainerWith2Images })}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${image?.fields?.caption}_${index}`}
                    id={`${image?.fields?.caption}_${index}`}
                >
                    <StyledWrapper>
                        <LazyImage
                            {...(fields?.generalLandingPage
                                ? { ...styles.generalLandingPageImage }
                                : { ...styles.image })}
                            src={image?.fields?.image}
                            altText="image"
                        />
                        <StyledWrapper {...styles.captionCss}>{image?.fields?.caption}</StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            ))}
        </GridContainer>
    );
};

const articlesDetails: WidgetModule = {
    component: INTArticlesImageGrid,
    definition: {
        group: "Common",
        displayName: "IntArticles Image Grid",
        fieldDefinitions: [
            {
                name: fields.numberOfImages,
                displayName: "Number of Images",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                sortOrder: 0,
                fieldType: "General",
            },
            {
                name: fields.generalLandingPage,
                displayName: "Is General Landing Page",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                sortOrder: 1,
                fieldType: "General",
            },
            {
                name: fields.imageCard,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.caption;
                },
                defaultValue: [],
                fieldType: "Translatable",
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: fields.caption,
                        displayName: "Caption",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: fields.image,
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 1,
                    },
                ],
            },
        ],
    },
};

export default articlesDetails;
